// widgets/AgentsPanel/AgentsPanel.tsx
// Panneau SUB-AGENTS — reconception du suivi des sous-agents (operation #31513, brief §4bis etage 7).
//
// Lit tasks.store (par selecteur) ET l'onglet actif de tabs.store (activeTabId -> sessionId). Filtre
// par session : n'affiche que les taches de la conversation active. Changer d'onglet change les
// agents affiches. MISE A JOUR SUR PLACE (la zone ne defile pas).
//
// PROJECTION : la duree affichee est `durationMs` du SDK, AFFICHEE TELLE QUELLE — jamais recalculee
// avec Date.now(). Il n'y a donc AUCUN ticker local : entre deux events la duree reste a la derniere
// valeur SDK (rafraichie ~1-2 s par les progressions), ce qui est acceptable et supprime le
// clignotement de l'ancien chronometre/timeout. Le re-render suit naturellement les ecritures du
// store (selecteur Zustand).
//
// Il ne connait NI Centrifugo, NI le terminal, NI les onglets autrement que par cette lecture :
// c'est ce qui le rend deplacable (brief §7).

import { useMemo } from 'react'
import { useTabsStore } from '@/shared/stores/tabs.store'
import { useTasksStore, type TaskState } from '@/shared/stores/tasks.store'

/** Statuts terminaux : la tache ne progresse plus, sa duree est figee. */
const TERMINAL_STATUSES = new Set(['completed', 'stopped', 'killed', 'failed', 'lost'])
/** Statuts de mort : le mot remplace la duree, en rouge (diagnostic #13, brief §4 decision 4). */
const DEATH_STATUSES = new Set(['killed', 'failed'])

/** Duree formatee `Xm Ys` (ou `Ys` sous la minute), sur le modele de la capture Fabrice. */
function formatDuration(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

/** Tokens formates `165.2k` au-dela de 1000, sinon la valeur brute. */
function formatTokens(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`
}

/** Couleur de la puce d'etat selon le statut. Sobre, MVP brut. */
function statusColor(status: string): string {
  if (DEATH_STATUSES.has(status)) return 'var(--color-danger, #e5484d)'
  if (TERMINAL_STATUSES.has(status)) return 'var(--color-text-faint)'
  return 'var(--color-success, #30a46c)'
}

/** Ligne d'une ligne mono, tronquee proprement (ellipsis, jamais de retour a la ligne). */
const clampLine = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
}

function AgentRow({ task }: { task: TaskState }) {
  const isDead = DEATH_STATUSES.has(task.status)
  const name = task.name ?? task.subagentType ?? 'agent'
  // Activite courante : ce que fait le sous-agent (currentActivity) + le dernier outil.
  const activity = [task.currentActivity, task.lastToolName].filter(Boolean).join(' · ')

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '4px var(--space-1)',
        fontSize: 12,
        fontFamily: 'var(--font-mono, monospace)',
        color: 'var(--color-text-muted)',
      }}
    >
      {/* Ligne 1 : puce d'etat + nom d'appel (stable). */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
        <span style={{ color: statusColor(task.status), flexShrink: 0 }}>●</span>
        <span style={{ color: 'var(--color-text)', ...clampLine }}>{name}</span>
      </div>

      {/* Ligne 2 : activite courante (ce qu'il fait) + dernier outil. Omise si vide. */}
      {activity && (
        <div style={{ paddingLeft: 'calc(var(--space-2) + 8px)', color: 'var(--color-text-faint)', ...clampLine }}>
          {activity}
        </div>
      )}

      {/* Ligne 3 : duree reelle (ou statut de mort en rouge) · nombre d'outils · tokens contexte. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 'var(--space-2)',
          paddingLeft: 'calc(var(--space-2) + 8px)',
          ...clampLine,
        }}
      >
        <span style={{ color: isDead ? 'var(--color-danger, #e5484d)' : 'var(--color-text-faint)', flexShrink: 0 }}>
          {isDead ? task.status : task.durationMs != null ? formatDuration(task.durationMs) : '—'}
        </span>
        {task.toolUses != null && (
          <span style={{ color: 'var(--color-text-faint)', flexShrink: 0 }}>· {task.toolUses} outils</span>
        )}
        {task.totalTokens != null && (
          <span style={{ color: 'var(--color-text-faint)', flexShrink: 0 }}>
            · ↓ {formatTokens(task.totalTokens)} contexte
          </span>
        )}
      </div>
    </div>
  )
}

export function AgentsPanel() {
  // Onglet actif -> sessionId de la conversation affichee. Selecteurs distincts, valeurs primitives.
  const sessionId = useTabsStore((s) => s.tabs.find((t) => t.id === s.activeTabId)?.sessionId)
  const sessionTasks = useTasksStore((s) => (sessionId ? s.sessions[sessionId] : undefined))

  const tasks = useMemo(() => (sessionTasks ? Object.values(sessionTasks) : []), [sessionTasks])

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: 'var(--space-2) var(--space-2) var(--space-3) 0',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          padding: 'var(--space-1) 0',
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--color-text-faint)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        Sub-agents
      </div>
      {tasks.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--color-text-faint)', padding: '2px var(--space-1)' }}>
          Aucun sous-agent
        </div>
      ) : (
        tasks.map((task) => <AgentRow key={task.taskId} task={task} />)
      )}
    </div>
  )
}
